'use client'

import MarketRateCard from '@/components/market/MarketRateCard'
import MarketInstrumentLabel from '@/components/market/MarketInstrumentLabel'
import MarketRateValue from '@/components/market/MarketRateValue'
import {
  getInstrumentMeta,
  resolveInstrumentKeys,
  type MarketInstrumentKey,
  type MarketPreset,
} from '@/lib/market-rates'
import { useMarketRates } from '@/lib/use-market-rates'

type MarketRatesStripProps = {
  instruments?: MarketInstrumentKey[]
  preset?: MarketPreset
  tags?: Array<{ slug?: string; tag?: string }>
  hideWhenEmpty?: boolean
  variant?: 'bar' | 'compact' | 'inline'
  linked?: boolean
  scrollable?: boolean
  live?: boolean
  className?: string
}

function MarketRatesStripSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="flex min-w-0 flex-1 gap-2 overflow-hidden py-0.5" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="flex min-w-[5rem] shrink-0 flex-col justify-center gap-1.5 rounded-[5px] bg-white px-2 py-1.5 dark:bg-slate-800 sm:h-[3.4375rem] sm:min-w-[5.5rem] sm:px-2.5 sm:py-2"
        >
          <div className="h-2.5 w-10 animate-pulse rounded bg-gray-200 dark:bg-slate-600" />
          <div className="flex items-center gap-1">
            <div className="h-3.5 w-11 animate-pulse rounded bg-gray-200 dark:bg-slate-600" />
            <div className="h-4 w-9 animate-pulse rounded bg-gray-200 dark:bg-slate-600" />
          </div>
        </div>
      ))}
    </div>
  )
}

export { MarketRatesStripSkeleton }

export default function MarketRatesStrip({
  instruments,
  preset = 'home',
  tags,
  hideWhenEmpty = false,
  variant = 'bar',
  linked = true,
  scrollable = false,
  live = true,
  className = '',
}: MarketRatesStripProps) {
  const { market, busy, loading } = useMarketRates({ live })
  const keys = resolveInstrumentKeys({ instruments, preset, tags })
  if (hideWhenEmpty && !keys.length) return null
  if (loading && !market) {
    return (
      <div
        className={className}
        role="status"
        aria-busy="true"
        aria-label="Piyasa verileri yükleniyor"
      >
        <MarketRatesStripSkeleton count={Math.min(keys.length || 6, 8)} />
      </div>
    )
  }

  const cardVariant = variant === 'compact' ? 'compact' : 'strip'

  if (variant === 'inline') {
    return (
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm ${className}`}>
        {keys.map((key) => {
          const meta = getInstrumentMeta(key)
          const row = market?.[key]
          return (
            <span key={key} className="inline-flex items-center gap-1 tabular-nums text-gray-700 dark:text-gray-300">
              <MarketInstrumentLabel instrumentKey={key} size="sm" className="text-gray-700 dark:text-gray-300 [&>span:first-child]:text-gray-700 [&>span:first-child]:dark:text-gray-300" />
              <span>:</span>
              <MarketRateValue
                instrumentKey={key}
                rate={row?.rate ?? null}
                decimals={meta.decimals}
                busy={busy}
                size="sm"
              />
            </span>
          )
        })}
      </div>
    )
  }

  if (scrollable) {
    return (
      <div
        className={`min-w-0 flex-1 overflow-x-auto py-0.5 [scrollbar-width:none] snap-x snap-mandatory [&::-webkit-scrollbar]:hidden ${className}`}
        aria-label="Piyasa verileri"
      >
        <div className="flex w-max min-w-full gap-1.5">
          {keys.map((key) => (
            <div key={key} className="w-auto shrink-0 snap-start">
              <MarketRateCard
                instrumentKey={key}
                row={market?.[key] ?? null}
                busy={busy}
                linked={linked}
                variant={cardVariant}
              />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`flex min-w-0 flex-wrap items-stretch gap-1 sm:gap-2 ${className}`}>
      {keys.map((key) => (
        <MarketRateCard
          key={key}
          instrumentKey={key}
          row={market?.[key] ?? null}
          busy={busy}
          linked={linked}
          variant={cardVariant}
        />
      ))}
    </div>
  )
}
